/**
 * HISIP-013: RubricBadge Component
 * 
 * Displays rubric pass/fail state with glow effects
 * Shows pass/fail indicator with optional tooltip and score
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Check, X, AlertTriangle } from 'lucide-react';
import { cn } from '@/utils/cn';

export interface RubricState {
  pass: boolean;
  score?: number;
  threshold?: number;
  fallback_threshold?: number;
  reason?: string;
  rubric_name?: string;
  rubric_version?: string;
}

interface RubricBadgeProps {
  rubric?: RubricState;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  showScore?: boolean;
  showTooltip?: boolean;
}

const RubricBadge: React.FC<RubricBadgeProps> = ({
  rubric,
  className,
  size = 'md',
  showScore = true,
  showTooltip = true
}) => {
  // No rubric state - show neutral
  if (!rubric || (rubric.score === undefined && rubric.pass === undefined)) {
    return (
      <div
        className={cn(
          "flex items-center justify-center rounded-full bg-gray-500/20 border border-gray-500/30",
          size === 'sm' && "w-5 h-5",
          size === 'md' && "w-6 h-6", 
          size === 'lg' && "w-8 h-8",
          className
        )}
        title={showTooltip ? "No rubric evaluation yet" : undefined}
      >
        <AlertTriangle 
          className={cn(
            "text-gray-400",
            size === 'sm' && "w-2.5 h-2.5",
            size === 'md' && "w-3 h-3",
            size === 'lg' && "w-4 h-4"
          )} 
        />
      </div>
    );
  }

  const passed = rubric.pass;
  const score = rubric.score;
  const threshold = rubric.threshold;

  // Build tooltip text with template details
  let tooltipText = '';
  if (showTooltip) {
    const templateInfo = rubric.rubric_name && rubric.rubric_version 
      ? `${rubric.rubric_name}@${rubric.rubric_version}`
      : 'Default rubric';
    
    if (score !== undefined && threshold !== undefined) {
      tooltipText = `${templateInfo}: ${score.toFixed(1)}% (threshold: ${threshold}%)`;
      if (rubric.fallback_threshold && rubric.fallback_threshold !== threshold) {
        tooltipText += ` (fallback: ${rubric.fallback_threshold}%)`;
      }
      if (rubric.reason) {
        tooltipText += ` - ${rubric.reason}`;
      }
    } else if (rubric.reason) {
      tooltipText = `${templateInfo} - ${rubric.reason}`;
    } else {
      tooltipText = `${templateInfo}: ${passed ? 'Passed' : 'Failed'}`;
    }
  }

  return (
    <div className="relative">
      {/* Glow effect for passed state */}
      {passed && (
        <motion.div
          className="absolute inset-0 rounded-full blur-md bg-emerald-400/40"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.4, 0.6, 0.4]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      )}
      
      {/* Main badge */}
      <motion.div
        className={cn(
          "relative flex items-center justify-center rounded-full border-2 transition-all duration-300",
          passed ? 
            "bg-emerald-500/20 border-emerald-400 text-emerald-400" : 
            "bg-red-500/20 border-red-400 text-red-400",
          size === 'sm' && "w-5 h-5",
          size === 'md' && "w-6 h-6",
          size === 'lg' && "w-8 h-8",
          className
        )}
        style={{
          boxShadow: passed ? 
            '0 0 12px rgba(52, 211, 153, 0.4), inset 0 0 6px rgba(52, 211, 153, 0.1)' :
            '0 0 8px rgba(248, 113, 113, 0.3), inset 0 0 4px rgba(248, 113, 113, 0.1)'
        }}
        title={tooltipText}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", duration: 0.3 }}
      >
        {passed ? (
          <Check 
            className={cn(
              "text-emerald-400",
              size === 'sm' && "w-2.5 h-2.5",
              size === 'md' && "w-3 h-3", 
              size === 'lg' && "w-4 h-4"
            )}
          />
        ) : (
          <X 
            className={cn(
              "text-red-400",
              size === 'sm' && "w-2.5 h-2.5",
              size === 'md' && "w-3 h-3",
              size === 'lg' && "w-4 h-4"
            )}
          />
        )}
      </motion.div>

      {/* Score display for larger sizes */}
      {showScore && score !== undefined && size !== 'sm' && (
        <motion.div
          className="absolute -bottom-1 -right-1 px-1 py-0.5 bg-black/80 text-white text-xs rounded font-mono"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          {Math.round(score)}%
        </motion.div>
      )}
    </div>
  );
};

export default RubricBadge;